// src/demo/useDemoModeFlag.js
//
// Tiny hook to look up subscriptions.demo_mode for the current user.
// Used by the app root to decide whether to mount DemoDataProvider +
// DemoModeShell around CommissionTracker.
//
// Caches the result for the session so we don't re-query on every
// re-render. Listens for a custom 'demo-mode-changed' event so the
// exit / replay endpoints can flip the local state without forcing
// a hard refresh.

import React from 'react';
import { supabase } from '../lib/supabase';

export default function useDemoModeFlag(userId) {
  const [state, setState] = React.useState({ loading: true, isDemo: false });

  const reload = React.useCallback(async () => {
    if (!userId) {
      setState({ loading: false, isDemo: false });
      return;
    }
    try {
      const { data } = await supabase
        .from('subscriptions')
        .select('demo_mode')
        .eq('user_id', userId)
        .maybeSingle();
      setState({ loading: false, isDemo: Boolean(data?.demo_mode) });
    } catch {
      // Fail safe: assume NOT demo if we can't determine. The seal
      // in api/_demoSeal.js fails closed at the boundary, so this
      // client-side fallback being permissive is fine.
      setState({ loading: false, isDemo: false });
    }
  }, [userId]);

  React.useEffect(() => { reload(); }, [reload]);

  // Cross-component event so /api/demo/exit can flip the flag
  // without a hard reload.
  React.useEffect(() => {
    function onChange() { reload(); }
    window.addEventListener('demo-mode-changed', onChange);
    return () => window.removeEventListener('demo-mode-changed', onChange);
  }, [reload]);

  return state;
}

/**
 * Fires the cross-component event so any mounted hook re-queries.
 * The /api/demo/exit handler should call this after a successful exit.
 */
export function notifyDemoModeChanged() {
  try { window.dispatchEvent(new CustomEvent('demo-mode-changed')); } catch {}
}
